/**
 * Dashboard API Service
 * Handles all API calls to Django dashboard endpoints using centralized ApiService
 */

import { apiService } from '../../../services/apiService';
import type { 
  DashboardInfo, 
  ChartData, 
  ScorecardData, 
  MedicalDirectorData,
  QualityDashboardData,
  QualityAlertDetailsResponse,
  DashboardFiltersPayload, 
  ExportRequestPayload 
} from '../types/dashboard.types';
import type { MasterDashboardData } from '../types/masterDashboard.types';

class DashboardApiService {
  /**
   * Get dashboard initialization data
   * Endpoint: POST /dashboard/get_dashboard_info
   */
  async getDashboardInfo(): Promise<DashboardInfo> {
    const response = await apiService.post<DashboardInfo>('/dashboard/get_dashboard_info');
    return response.data;
  }

  /**
   * Get chart data for specified filters
   * Endpoint: POST /dashboard/get_encounter_range
   */
  async getEncounterRange(filters: DashboardFiltersPayload): Promise<ChartData> {
    const response = await apiService.post<ChartData>('/dashboard/get_encounter_range', filters);
    return response.data;
  }


  /**
   * Export encounter report as CSV
   * Endpoint: POST /reports/generate_encounter_report
   * Returns blob for download
   * 
   * Note: This endpoint returns a blob, so we handle it specially
   * rather than using the centralized apiService which expects JSON
   */
  async exportEncounterReport(payload: ExportRequestPayload): Promise<Blob> {
    // For blob endpoints, we need to handle CSRF manually since apiService expects JSON responses
    const csrfToken = this.getCSRFToken();
    
    const response = await fetch('/reports/generate_encounter_report', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': csrfToken,
      },
      credentials: 'include', // Updated to match apiService pattern
      body: JSON.stringify(payload),
    });

    // Handle session expiration - redirect to login
    if (response.status === 401 || response.status === 403) {
      const currentPath = window.location.pathname + window.location.search;
      window.location.href = `/login?next=${encodeURIComponent(currentPath)}`;
      throw new Error('Session expired');
    }

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Export functionality is not yet available. The report generation endpoint has not been implemented on this server.');
      }
      throw new Error(`Export error: ${response.status} ${response.statusText}`);
    }

    return response.blob();
  }

  /**
   * Update broadcast message log
   * Endpoint: POST /dashboard/update_broadcast_log
   */
  async updateBroadcastLog(messageId: number, hasUserSeen: boolean): Promise<void> {
    await apiService.post('/dashboard/update_broadcast_log', { 
      broadcast_id: messageId,
      has_user_seen: hasUserSeen 
    });
  }

  /**
   * Get scorecards data
   * Endpoint: POST /dashboard/get_scorecards_info
   */
  async getScorecards(payload: DashboardFiltersPayload): Promise<ScorecardData> {
    const response = await apiService.post<ScorecardData>('/dashboard/get_scorecards_info', payload);
    return response.data;
  }

  /**
   * Get Medical Director dashboard data
   * Endpoint: POST /dashboard/get_medical_director_dashboard
   */
  async getMedicalDirectorDashboard(payload: DashboardFiltersPayload & { days_run?: number }): Promise<MedicalDirectorData> {
    const response = await apiService.post<MedicalDirectorData>('/dashboard/get_medical_director_dashboard', payload);
    return response.data;
  }

  async getQualityDashboard(
    dateRange: { start: string; end: string },
    facilityIds: number[],
    alertPromptIds: number[],
  ): Promise<QualityDashboardData> {
    const response = await apiService.post<QualityDashboardData>(
      '/dashboard/get_quality_dashboard',
      {
        start_date: DashboardApiService.formatDateForAPI(new Date(dateRange.start)),
        end_date: DashboardApiService.formatDateForAPI(new Date(dateRange.end)),
        facility_ids: facilityIds,
        alert_prompt_ids: alertPromptIds,
      },
    );
    return response.data;
  }

  async getQualityDashboardDetails(params: {
    dateRange: { start: string; end: string };
    facilityIds: number[];
    alertPromptIds: number[];
    drilldownType: 'provider' | 'alert' | 'attending_source';
    drilldownId: number | string;
    page: number;
    pageSize?: number;
  }): Promise<QualityAlertDetailsResponse> {
    const response = await apiService.post<QualityAlertDetailsResponse>(
      '/dashboard/get_quality_dashboard_details',
      {
        start_date: DashboardApiService.formatCalendarDateForAPI(params.dateRange.start),
        end_date: DashboardApiService.formatCalendarDateForAPI(params.dateRange.end),
        facility_ids: params.facilityIds,
        alert_prompt_ids: params.alertPromptIds,
        drilldown_type: params.drilldownType,
        drilldown_id: params.drilldownId,
        page: params.page,
        page_size: params.pageSize ?? 20,
      },
    );
    return response.data;
  }

  /**
   * Update provider watch list
   * Endpoint: POST /dashboard/update_watches
   */
  async updateWatches(watchedProviderIds: number[]): Promise<void> {
    const watched_providers = watchedProviderIds.map(id => ({ employee_id: id }));
    await apiService.post('/dashboard/update_watches', { watched_providers });
  }

  /**
   * Get master operations dashboard data for tec_base / ops host
   * Endpoint: POST /admin/get_master_dashboard_data
   */
  async getMasterDashboardData(includeInactive: boolean, billingMonth: string): Promise<MasterDashboardData> {
    const response = await apiService.post<MasterDashboardData>('/admin/get_master_dashboard_data', {
      include_inactive: includeInactive,
      billing_month: billingMonth,
    });
    return response.data;
  }

  /*
   * Get CSRF token using same logic as centralized apiService
   * Only used for blob endpoints that can't use the centralized service
   */
  private getCSRFToken(): string {
    // Use same CSRF token logic as apiService for consistency
    const globalContext = (window as any).globalContext;
    if (globalContext?.authContext?.csrf_token) {
      return globalContext.authContext.csrf_token;
    }
    
    // Fallback: get from cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return decodeURIComponent(value);
      }
    }
    
    throw new Error('CSRF token not found');
  }

  /**
   * Format date for Django API (UTC neutral format)
   */
  static formatDateForAPI(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}T00:00:00.00Z`;
  }

  static formatCalendarDateForAPI(value: string): string {
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return `${value}T00:00:00.00Z`;
    }
    return this.formatDateForAPI(new Date(value));
  }

  /**
   * Create filter payload from UI filters
   * Note: dateRange values are ISO strings for Redux serialization
   */
  static createFiltersPayload(filters: { facilityId: number | null; dateRange: { start: string; end: string } }): DashboardFiltersPayload {
    return {
      start_date: this.formatDateForAPI(new Date(filters.dateRange.start)),
      end_date: this.formatDateForAPI(new Date(filters.dateRange.end)),
      facility_id: filters.facilityId,
      user_id: (window as any).authContext?.employee?.user_id,
    };
  }
}

// Export class for static methods and singleton instance
export { DashboardApiService };
export const dashboardApi = new DashboardApiService();
export default dashboardApi;
